import React, { useState } from 'react';
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  CardActions,
  Button,
  Switch,
  FormControlLabel,
  Chip,
  Avatar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
  Divider,
  Tab,
  Tabs,
  InputAdornment,
  IconButton
} from '@mui/material';
import {
  GitHub as GitHubIcon,
  Code as BitbucketIcon,
  Psychology as AIIcon,
  Business as BusinessIcon,
  Analytics as AnalyticsIcon,
  Cloud as CloudIcon,
  Support as SupportIcon,
  Settings as SettingsIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
  CheckCircle as ConnectedIcon,
  RadioButtonUnchecked as DisconnectedIcon,
  Warning as WarningIcon,
  Source as GitLabIcon
} from '@mui/icons-material';
import { useLoading } from '../../../contexts/LoadingContext';
import { useNotify } from '../../ui/NotificationSystem';

interface Integration {
  id: string;
  name: string;
  description: string;
  category: 'development' | 'ai' | 'project-management' | 'cicd' | 'analytics' | 'business' | 'communication';
  icon: React.ReactNode;
  status: 'connected' | 'disconnected' | 'error';
  isEnabled: boolean;
  requiresApiKey: boolean;
  requiresOAuth: boolean;
  configFields?: {
    name: string;
    label: string;
    type: 'text' | 'password' | 'url';
    required: boolean;
    value?: string;
  }[];
  lastSync?: string;
  webhookUrl?: string;
}

const INTEGRATIONS: Integration[] = [
  // Development & Code Management
  {
    id: 'github',
    name: 'GitHub',
    description: 'Connect your GitHub repositories for seamless code management and collaboration.',
    category: 'development',
    icon: <GitHubIcon />,
    status: 'disconnected',
    isEnabled: false,
    requiresApiKey: true,
    requiresOAuth: true,
    configFields: [
      { name: 'apiKey', label: 'Personal Access Token', type: 'password', required: true },
      { name: 'organization', label: 'Organization (optional)', type: 'text', required: false }
    ]
  },
  {
    id: 'bitbucket',
    name: 'Bitbucket',
    description: 'Integrate with Bitbucket for Git repository management and CI/CD pipelines.',
    category: 'development',
    icon: <BitbucketIcon />,
    status: 'disconnected',
    isEnabled: false,
    requiresApiKey: true,
    requiresOAuth: true,
    configFields: [
      { name: 'username', label: 'Username', type: 'text', required: true },
      { name: 'appPassword', label: 'App Password', type: 'password', required: true },
      { name: 'workspace', label: 'Workspace', type: 'text', required: true }
    ]
  },
  {
    id: 'gitlab',
    name: 'GitLab',
    description: 'Connect to GitLab for repository management and DevOps workflows.',
    category: 'development',
    icon: <GitLabIcon />,
    status: 'disconnected',
    isEnabled: false,
    requiresApiKey: true,
    requiresOAuth: false,
    configFields: [
      { name: 'apiKey', label: 'Personal Access Token', type: 'password', required: true },
      { name: 'instanceUrl', label: 'GitLab Instance URL', type: 'url', required: true, value: 'https://gitlab.com' }
    ]
  },

  // AI & Development Tools
  {
    id: 'github-copilot',
    name: 'GitHub Copilot',
    description: 'AI-powered code completion and programming assistance.',
    category: 'ai',
    icon: <AIIcon />,
    status: 'disconnected',
    isEnabled: false,
    requiresApiKey: false,
    requiresOAuth: true
  },
  {
    id: 'openai',
    name: 'OpenAI',
    description: 'Integrate OpenAI GPT models for AI-powered features and automation.',
    category: 'ai',
    icon: <AIIcon />,
    status: 'disconnected',
    isEnabled: false,
    requiresApiKey: true,
    requiresOAuth: false,
    configFields: [
      { name: 'apiKey', label: 'API Key', type: 'password', required: true },
      { name: 'organization', label: 'Organization ID (optional)', type: 'text', required: false }
    ]
  },
  {
    id: 'gemini',
    name: 'Google Gemini',
    description: 'Google\'s AI model for advanced language processing and generation.',
    category: 'ai',
    icon: <AIIcon />,
    status: 'disconnected',
    isEnabled: false,
    requiresApiKey: true,
    requiresOAuth: false,
    configFields: [
      { name: 'apiKey', label: 'API Key', type: 'password', required: true }
    ]
  },

  // Business & Analytics
  {
    id: 'stripe',
    name: 'Stripe',
    description: 'Payment processing and subscription management.',
    category: 'business',
    icon: <BusinessIcon />,
    status: 'disconnected',
    isEnabled: false,
    requiresApiKey: true,
    requiresOAuth: false,
    configFields: [
      { name: 'publishableKey', label: 'Publishable Key', type: 'text', required: true },
      { name: 'secretKey', label: 'Secret Key', type: 'password', required: true },
      { name: 'webhookSecret', label: 'Webhook Secret', type: 'password', required: false }
    ]
  },
  {
    id: 'google-analytics',
    name: 'Google Analytics',
    description: 'Web analytics and user behavior tracking.',
    category: 'analytics',
    icon: <AnalyticsIcon />,
    status: 'disconnected',
    isEnabled: false,
    requiresApiKey: true,
    requiresOAuth: true,
    configFields: [
      { name: 'measurementId', label: 'Measurement ID', type: 'text', required: true },
      { name: 'apiSecret', label: 'API Secret', type: 'password', required: false }
    ]
  }
];

const CATEGORIES = {
  development: { label: 'Development & Code', icon: <GitHubIcon /> },
  ai: { label: 'AI & Automation', icon: <AIIcon /> },
  'project-management': { label: 'Project Management', icon: <BusinessIcon /> },
  cicd: { label: 'CI/CD & Deployment', icon: <CloudIcon /> },
  analytics: { label: 'Analytics & Monitoring', icon: <AnalyticsIcon /> },
  business: { label: 'Business & Payments', icon: <BusinessIcon /> },
  communication: { label: 'Communication & Support', icon: <SupportIcon /> }
};

export const IntegrationsPage: React.FC = () => {
  const [integrations, setIntegrations] = useState<Integration[]>(INTEGRATIONS);
  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  const [configDialog, setConfigDialog] = useState<{
    open: boolean;
    integration: Integration | null;
  }>({ open: false, integration: null });
  const [configValues, setConfigValues] = useState<Record<string, string>>({});
  const [showPasswords, setShowPasswords] = useState<Record<string, boolean>>({});
  
  const { setLoading } = useLoading();
  const { success: showSuccess, error: showError } = useNotify();

  const filteredIntegrations = selectedCategory === 'all' 
    ? integrations 
    : integrations.filter(integration => integration.category === selectedCategory);

  const handleToggleIntegration = async (integrationId: string, enabled: boolean) => {
    setLoading('integrations.toggle', true);
    try {
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      setIntegrations(prev => prev.map(integration => 
        integration.id === integrationId 
          ? { ...integration, isEnabled: enabled, status: enabled ? 'connected' : 'disconnected' }
          : integration
      ));
      
      showSuccess(
        `${integrations.find(i => i.id === integrationId)?.name} ${enabled ? 'enabled' : 'disabled'} successfully`
      );
    } catch (error) {
      showError('Failed to update integration');
    } finally {
      setLoading('integrations.toggle', false);
    }
  };

  const handleConfigureIntegration = (integration: Integration) => {
    setConfigDialog({ open: true, integration });
    
    // Initialize config values
    const initialValues: Record<string, string> = {};
    integration.configFields?.forEach(field => {
      initialValues[field.name] = field.value || '';
    });
    setConfigValues(initialValues);
  };

  const handleSaveConfiguration = async () => {
    if (!configDialog.integration) return;

    setLoading('integrations.configure', true);
    try {
      // Validate required fields
      const requiredFields = configDialog.integration.configFields?.filter(field => field.required) || [];
      const missingFields = requiredFields.filter(field => !configValues[field.name]?.trim());
      
      if (missingFields.length > 0) {
        showError(`Please fill in required fields: ${missingFields.map(f => f.label).join(', ')}`);
        return;
      }

      // Simulate API call to save configuration
      await new Promise(resolve => setTimeout(resolve, 1500));
      
      setIntegrations(prev => prev.map(integration => 
        integration.id === configDialog.integration!.id 
          ? { 
              ...integration, 
              status: 'connected', 
              isEnabled: true,
              lastSync: new Date().toISOString(),
              configFields: integration.configFields?.map(field => ({
                ...field,
                value: configValues[field.name] || field.value
              }))
            }
          : integration
      ));
      
      showSuccess(`${configDialog.integration.name} configured successfully`);
      setConfigDialog({ open: false, integration: null });
      setConfigValues({});
    } catch (error) {
      showError('Failed to save configuration');
    } finally {
      setLoading('integrations.configure', false);
    }
  };

  const getStatusColor = (status: Integration['status']) => {
    switch (status) {
      case 'connected': return 'success';
      case 'error': return 'error';
      default: return 'default';
    }
  };

  const getStatusIcon = (status: Integration['status']) => {
    switch (status) {
      case 'connected': return <ConnectedIcon color="success" />;
      case 'error': return <WarningIcon color="error" />;
      default: return <DisconnectedIcon color="disabled" />;
    }
  };

  return (
    <Box>
      <Box sx={{ mb: 3 }}>
        <Typography variant="h4" gutterBottom>
          Integrations
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Connect VegaDigital with your favorite tools and services to streamline your workflow.
        </Typography>
      </Box>

      {/* Category Filter Tabs */}
      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
        <Tabs 
          value={selectedCategory} 
          onChange={(e, value) => setSelectedCategory(value)}
          variant="scrollable"
          scrollButtons="auto"
        >
          <Tab label="All" value="all" />
          {Object.entries(CATEGORIES).map(([key, category]) => (
            <Tab 
              key={key}
              label={category.label} 
              value={key}
              icon={category.icon}
              iconPosition="start"
            />
          ))}
        </Tabs>
      </Box>

      {/* Integrations Grid */}
      <Grid container spacing={3}>
        {filteredIntegrations.map((integration) => (
          <Grid size={{ xs: 12, md: 6, lg: 4 }} key={integration.id}>
            <Card 
              sx={{ 
                height: '100%', 
                display: 'flex', 
                flexDirection: 'column',
                border: integration.isEnabled ? 2 : 1,
                borderColor: integration.isEnabled ? 'primary.main' : 'divider'
              }}
            >
              <CardContent sx={{ flexGrow: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <Avatar sx={{ mr: 2, bgcolor: 'primary.main' }}>
                    {integration.icon}
                  </Avatar>
                  <Box sx={{ flexGrow: 1 }}>
                    <Typography variant="h6">
                      {integration.name}
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      {getStatusIcon(integration.status)}
                      <Chip 
                        label={integration.status}
                        size="small"
                        color={getStatusColor(integration.status)}
                        variant="outlined"
                      />
                    </Box>
                  </Box>
                </Box>
                
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  {integration.description}
                </Typography>

                {integration.lastSync && (
                  <Typography variant="caption" color="text.secondary">
                    Last sync: {new Date(integration.lastSync).toLocaleString()}
                  </Typography>
                )}
              </CardContent>
              
              <CardActions sx={{ justifyContent: 'space-between', px: 2, pb: 2 }}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={integration.isEnabled}
                      onChange={(e) => handleToggleIntegration(integration.id, e.target.checked)}
                      disabled={integration.status === 'error'}
                    />
                  }
                  label="Enable"
                />
                
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<SettingsIcon />}
                  onClick={() => handleConfigureIntegration(integration)}
                  disabled={!integration.requiresApiKey && !integration.requiresOAuth}
                >
                  Configure
                </Button>
              </CardActions>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Configuration Dialog */}
      <Dialog 
        open={configDialog.open} 
        onClose={() => setConfigDialog({ open: false, integration: null })}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          Configure {configDialog.integration?.name}
        </DialogTitle>
        <DialogContent>
          {configDialog.integration?.requiresOAuth && (
            <Alert severity="info" sx={{ mb: 2 }}>
              This integration requires OAuth authentication. You'll be redirected to authorize VegaDigital.
            </Alert>
          )}
          
          {configDialog.integration?.configFields?.map((field) => (
            <TextField
              key={field.name}
              fullWidth
              margin="normal"
              label={field.label}
              type={field.type === 'password' && !showPasswords[field.name] ? 'password' : 'text'}
              required={field.required}
              value={configValues[field.name] || ''}
              onChange={(e) => setConfigValues(prev => ({
                ...prev,
                [field.name]: e.target.value
              }))}
              InputProps={field.type === 'password' ? {
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setShowPasswords(prev => ({
                        ...prev,
                        [field.name]: !prev[field.name]
                      }))}
                      edge="end"
                    >
                      {showPasswords[field.name] ? <VisibilityOffIcon /> : <VisibilityIcon />}
                    </IconButton>
                  </InputAdornment>
                )
              } : undefined}
            />
          ))}

          {configDialog.integration?.webhookUrl && (
            <Box sx={{ mt: 2 }}>
              <Divider sx={{ mb: 2 }} />
              <Typography variant="subtitle2" gutterBottom>
                Webhook URL
              </Typography>
              <TextField
                fullWidth
                value={`${window.location.origin}/api/webhooks/${configDialog.integration.id}`}
                InputProps={{ readOnly: true }}
                helperText="Use this URL in your service's webhook configuration"
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfigDialog({ open: false, integration: null })}>
            Cancel
          </Button>
          <Button onClick={handleSaveConfiguration} variant="contained">
            Save Configuration
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default IntegrationsPage;